import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { ButtonGroup } from "@/components/ui/button-group";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";
import { ExportMarkersForm } from "../../storyproducer/components/ExportMarkersDialog";
import AssemblyManager from "../../storyproducer/components/AssemblyManager";
import { SummaryFullView } from "../../executive/views/SummaryFullView";

const Discovery = React.lazy(() =>
  import("../../discovery/views/Discovery").then((m) => ({
    default: m.Discovery,
  })),
);
const StoryProducer = React.lazy(() =>
  import("../../storyproducer/views/StoryProducer").then((m) => ({
    default: m.StoryProducer,
  })),
);

export const EditorLayout: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject, clearActiveProject } = useProjects();
  const { navigate } = useProjectNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showDiscover, setShowDiscover] = useState(false);
  const [showAssembly, setShowAssembly] = useState(false);
  const [showSummaries, setShowSummaries] = useState(false);

  const VALID_TABS = ["explore", "export", "none"] as const;
  type Tab = (typeof VALID_TABS)[number];
  const tabParam = searchParams.get("tab");
  const activeTab: Tab = VALID_TABS.includes(tabParam as Tab) ? (tabParam as Tab) : "none"; // Default: no center panel — VTR opens instead
  const [showVtr, setShowVtr] = useState(true); // VTR is the default first panel
  const setActiveTab = (tab: Tab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (tab === "none") {
          params.set("tab", "none");
        } else {
          params.set("tab", tab);
        }
        return params;
      },
      { replace: true },
    );
  };

  const handleNavigateToLog = (
    logId: string,
    storyId: string,
    _highlightText?: string,
    citationType?: string,
    citationTc?: string,
  ) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "explore");
        params.set("storyId", storyId);
        params.set("logId", logId);
        if (citationType) params.set("type", citationType);
        if (citationTc) params.set("tc", citationTc);
        return params;
      },
      { replace: true },
    );
  };

  const ACCENT_COLOR = "var(--color-role-editor)";

  if (!activeProject) return null;

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg-dark, #000)",
        color: "var(--color-text-primary)",
        fontFamily: "var(--font-family)",
        overflow: "hidden",
      }}
      data-testid="editor-layout"
    >
      <header
        style={{
          height: "88px",
          boxSizing: "border-box",
          padding: "0 min(40px, 3vw)",
          borderBottom: `5px solid ${ACCENT_COLOR}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--color-panel-bg)",
          backdropFilter: "blur(10px)",
          boxShadow: `0 0 20px ${ACCENT_COLOR}15`,
          zIndex: 100,
          position: "relative",
        }}
      >
        {/* LEFT: Logo & Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <RoleBrandingHeader
            role="editor"
            roleLabel={t("auth.roles.editor").toUpperCase()}
            projectName={activeProject.name}
            onHomeClick={() => {
              clearActiveProject();
              navigate("/projects");
            }}
            testIdPrefix="editor"
          />
        </div>

        {/* CENTER: Navigation Buttons */}
        <div
          data-testid="editor-nav"
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <div
            data-testid="editor-nav-tabs"
            style={{
              display: "flex",
              gap: "8px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "12px",
              padding: "6px",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <button
              onClick={() => setShowDiscover((prev) => !prev)}
              style={{
                padding: "8px 20px",
                background: showDiscover ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showDiscover ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-brain" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.discover", "BRAIN")}
            </button>
            <button
              onClick={() => setActiveTab(activeTab === "explore" ? "none" : "explore")}
              style={{
                padding: "8px 20px",
                background: activeTab === "explore" ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: activeTab === "explore" ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-compass" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.explore", "LIBRARY")}
            </button>
            <button
              onClick={() => setShowAssembly((prev) => !prev)}
              style={{
                padding: "8px 20px",
                background: showAssembly ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showAssembly ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-cut" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.cut", "CUT")}
            </button>
            <button
              onClick={() => setShowVtr((prev) => !prev)}
              style={{
                padding: "8px 20px",
                background: showVtr ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showVtr ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-film" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.approved", "VTR")}
            </button>
            <button
              onClick={() => setActiveTab(activeTab === "export" ? "none" : "export")}
              style={{
                padding: "8px 20px",
                background: activeTab === "export" ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: activeTab === "export" ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-download" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.export", "EXPORT")}
            </button>
            <button
              data-testid="editor-toggle-summaries"
              onClick={() => setShowSummaries((prev) => !prev)}
              style={{
                padding: "8px 20px",
                background: showSummaries ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showSummaries ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-bar-chart-alt-2" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.summaries", "SUMMARIES")}
            </button>
          </div>
        </div>

        {/* RIGHT: User & Menu */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          <ButtonGroup>
            <LanguageToggle />
            <BugReportButton module="editor" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>
          <ConnectionStatusWidget />
          <UserMenu role="editor" testIdPrefix="editor-user" />
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <div
        data-testid="editor-content"
        style={
          {
            flex: 1,
            display: "flex",
            overflow: "hidden",
            padding: "20px",
            gap: "20px",
            // Force Editor Purple for sub-modules if needed
            "--color-accent-primary": "var(--color-role-editor)",
            "--color-accent-secondary": "var(--color-role-editor)",
            "--color-btn-primary": "var(--color-role-editor)",
            "--color-btn-hover": "rgba(168, 85, 247, 0.1)", // Editor color hover
            "--color-highlight": "rgba(168, 85, 247, 0.2)",
            "--color-brand": "var(--color-role-editor)",
            "--discovery-accent": "var(--color-role-editor)",
          } as React.CSSProperties
        }
      >
        {/* Panel LEFT: DISCOVERY */}
        <div
          style={{
            flex: showDiscover ? 1 : 0,
            width: showDiscover ? "auto" : 0,
            minWidth: showDiscover ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showDiscover ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showDiscover ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showDiscover ? "auto" : "none",
          }}
        >
          <React.Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "rgba(255,255,255,0.3)",
                }}
              >
                Loading...
              </div>
            }
          >
            <Discovery isEmbedded={true} onNavigateToLog={handleNavigateToLog} />
          </React.Suspense>
        </div>

        {/* Panel CENTER: ACTIVE TAB CONTENT */}
        <div
          style={{
            flex: activeTab !== "none" ? 1 : 0,
            width: activeTab !== "none" ? "auto" : 0,
            display: "flex",
            flexDirection: "column",
            background: "var(--color-panel-bg)",
            border: activeTab !== "none" ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            overflow: "hidden",
            opacity: activeTab !== "none" ? 1 : 0,
            pointerEvents: activeTab !== "none" ? "auto" : "none",
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
          }}
        >
          {activeTab === "explore" && (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <React.Suspense
                fallback={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    Loading...
                  </div>
                }
              >
                <StoryProducer isEmbedded={true} />
              </React.Suspense>
            </div>
          )}

          {activeTab === "export" && (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  maxWidth: "640px",
                  width: "100%",
                  background: "var(--color-panel-bg)",
                  border: "1px solid var(--color-panel-border)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "24px",
                  textAlign: "center",
                  height: "fit-content",
                  marginTop: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "3rem",
                    color: "var(--color-role-editor)",
                  }}
                >
                  <i className="bx bx-download"></i>
                </div>
                <h2
                  style={{
                    margin: 0,
                    color: "var(--color-role-editor)",
                    letterSpacing: "1px",
                    fontSize: "1.4rem",
                  }}
                >
                  {t("editor.title", "Exportar markers")}
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: "var(--color-text-muted)",
                    fontSize: "0.95rem",
                    maxWidth: "480px",
                  }}
                >
                  {t(
                    "logger.export.description",
                    "AVID Media Composer (.txt). Selecciona el rango de hora de logueo.",
                  )}
                </p>

                <div
                  style={{
                    width: "100%",
                    maxWidth: "480px",
                    textAlign: "left",
                    background: "var(--color-panel-bg)",
                    border: "1px solid var(--color-panel-border)",
                    borderRadius: "var(--border-radius-md)",
                    padding: "24px",
                    marginTop: "8px",
                  }}
                >
                  <ExportMarkersForm
                    projectId={activeProject.id}
                    projectName={activeProject.name}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Panel RIGHT: CUT / ASSEMBLY */}
        <div
          style={{
            flex: showAssembly ? 1 : 0,
            width: showAssembly ? "auto" : 0,
            minWidth: showAssembly ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showAssembly ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showAssembly ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showAssembly ? "auto" : "none",
          }}
        >
          <AssemblyManager />
        </div>

        {/* Panel: VTR (Approved Projects) */}
        <div
          style={{
            flex: showVtr ? 1 : 0,
            width: showVtr ? "auto" : 0,
            minWidth: showVtr ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showVtr ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showVtr ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showVtr ? "auto" : "none",
          }}
        >
          <AssemblyManager showOnlyApproved={true} />
        </div>
      </div>

      {/* SUMMARIES FULL-SCREEN OVERLAY */}
      {showSummaries && activeProject?.id && (
        <div
          data-testid="editor-summaries-fullscreen"
          style={{
            position: "fixed",
            top: 88,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            background: "#000",
            overflow: "auto",
            padding: "24px 28px",
          }}
        >
          <SummaryFullView projectId={activeProject.id} accentColor={ACCENT_COLOR} />
        </div>
      )}
    </div>
  );
};

export default EditorLayout;
